/**
 * Delete Account Dialog (Story 0.8 - GDPR)
 *
 * Multi-step confirmation dialog for account deletion:
 * Step 1: Warning + type "DELETE" to confirm
 * Step 2: Password re-authentication
 * Step 3: Processing state
 *
 * AC: #2, #3, #4, #11, #18
 */

import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  Button,
  Input,
} from "@provenleap/ui";
import { apiClient } from "../../lib/apiClient";
import { logout } from "../../lib/auth";


interface DeleteAccountDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Whether the user is sole owner of a tenant with other members */
  hasTenantMembers?: boolean;
  /** Date of last deletion cancellation (ISO string), for AC#18 re-request warning */
  deletionCancelledAt?: string | null;
}

interface DeleteAccountResponse {
  data: {
    message: string;
    scheduled_deletion_date: string;
    warning?: {
      soleOwnerTenants: Array<{ tenantId: string; memberCount: number }>;
    };
  };
}

export function DeleteAccountDialog({
  open,
  onOpenChange,
  hasTenantMembers = false,
  deletionCancelledAt,
}: DeleteAccountDialogProps) {
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [deleteInput, setDeleteInput] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);

  const isDeleteConfirmed = deleteInput === "DELETE";

  // Check if re-request within 30 days (AC#18)
  const showReRequestWarning = (() => {
    if (!deletionCancelledAt) return false;
    const cancelledDate = new Date(deletionCancelledAt);
    const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);
    return cancelledDate > thirtyDaysAgo;
  })();

  const deletionMutation = useMutation({
    mutationFn: async (data: {
      password: string;
      confirmTenantDeletion?: boolean;
    }) => {
      const response = await apiClient("/api/account", {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (!response.ok) {
        const errorBody = await response.json();
        if (response.status === 403) {
          throw new Error("Incorrect password. Please try again.");
        }
        if (response.status === 409) {
          throw new Error("Account is already scheduled for deletion.");
        }
        if (response.status === 429) {
          throw new Error(
            "Too many attempts. Please wait 15 minutes and try again."
          );
        }
        throw new Error(
          (errorBody as { detail?: string }).detail ||
            "Failed to delete account"
        );
      }
      return (await response.json()) as DeleteAccountResponse;
    },
    onMutate: () => {
      setStep(3);
      setError(null);
    },
    onSuccess: async () => {
      // Use window.location for deletion redirect because the _authenticated
      // route guard races with logout's clearAuth() and redirects to /login
      // before TanStack navigate() can fire.
      await logout(undefined);
      window.location.href = "/deletion-confirmed";
    },
    onError: (err: Error) => {
      setError(err.message);
      // Go back to password step on error (unless rate limited)
      if (err.message.includes("Too many attempts")) {
        setStep(1);
        resetForm();
      } else {
        setStep(2);
      }
    },
  });

  function resetForm() {
    setStep(1);
    setDeleteInput("");
    setPassword("");
    setError(null);
  }

  function handleOpenChange(newOpen: boolean) {
    if (!newOpen) {
      resetForm();
    }
    onOpenChange(newOpen);
  }

  function handleConfirmDelete() {
    if (!isDeleteConfirmed) return;
    setStep(2);
    setError(null);
  }

  function handleSubmitPassword(e: React.FormEvent) {
    e.preventDefault();
    if (!password) return;
    deletionMutation.mutate({
      password,
      confirmTenantDeletion: hasTenantMembers ? true : undefined,
    });
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="sm:max-w-[400px]"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        {step === 1 && (
          <>
            <DialogHeader>
              <DialogTitle className="text-destructive">
                Delete Account
              </DialogTitle>
              <DialogDescription>
                This action cannot be undone after the 14-day grace period. All
                your data will be permanently deleted.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              {/* Deletion scope warning */}
              <div className="rounded-md border border-destructive/50 bg-destructive/5 p-3 text-sm">
                <p className="font-medium text-destructive mb-2">
                  The following will be deleted:
                </p>
                <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                  <li>All clients and their data</li>
                  <li>All connected integrations (OAuth tokens revoked)</li>
                  <li>All reports and PDFs</li>
                  <li>All cached data</li>
                  <li>Your user profile and account</li>
                </ul>
              </div>

              {/* Sole-owner tenant warning (AC#11) */}
              {hasTenantMembers && (
                <div
                  className="rounded-md border border-destructive bg-destructive/10 p-3 text-sm"
                  role="alert"
                >
                  <p className="font-medium text-destructive">
                    Warning: Your workspace has other members
                  </p>
                  <p className="text-muted-foreground mt-1">
                    As the sole owner, deleting your account will also delete the
                    workspace and remove access for all other members.
                  </p>
                </div>
              )}

              {/* Re-request warning (AC#18) */}
              {showReRequestWarning && (
                <div
                  className="rounded-md border border-amber-500 bg-amber-50 p-3 text-sm dark:bg-amber-950/20"
                  role="alert"
                >
                  <p className="font-medium text-amber-700 dark:text-amber-400">
                    You recently cancelled a deletion request
                  </p>
                  <p className="text-muted-foreground mt-1">
                    Your previous deletion was cancelled on{" "}
                    {new Date(deletionCancelledAt!).toLocaleDateString()}.
                    Are you sure you want to proceed?
                  </p>
                </div>
              )}

              {/* Type DELETE confirmation (AC#3) */}
              <div>
                <label
                  htmlFor="delete-confirm"
                  className="block text-sm font-medium mb-1"
                >
                  Type <span className="font-bold">DELETE</span> to confirm
                </label>
                <Input
                  id="delete-confirm"
                  value={deleteInput}
                  onChange={(e) => setDeleteInput(e.target.value)}
                  placeholder="Type DELETE"
                  autoComplete="off"
                  data-testid="delete-confirm-input"
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => handleOpenChange(false)}>
                Cancel
              </Button>
              <Button
                variant="destructive"
                disabled={!isDeleteConfirmed}
                onClick={handleConfirmDelete}
                data-testid="confirm-delete-btn"
              >
                Continue
              </Button>
            </DialogFooter>
          </>
        )}

        {step === 2 && (
          <>
            <DialogHeader>
              <DialogTitle className="text-destructive">
                Confirm Your Identity
              </DialogTitle>
              <DialogDescription>
                Enter your password to proceed with account deletion.
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleSubmitPassword} className="space-y-4">
              {error && (
                <div
                  className="rounded-md border border-destructive bg-destructive/5 p-3 text-sm text-destructive"
                  role="alert"
                >
                  {error}
                </div>
              )}

              <div>
                <label
                  htmlFor="delete-password"
                  className="block text-sm font-medium mb-1"
                >
                  Password
                </label>
                <Input
                  id="delete-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  autoComplete="current-password"
                  data-testid="delete-password-input"
                />
              </div>

              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setStep(1);
                    setPassword("");
                    setError(null);
                  }}
                >
                  Back
                </Button>
                <Button
                  type="submit"
                  variant="destructive"
                  disabled={!password || deletionMutation.isPending}
                  data-testid="submit-delete-btn"
                >
                  {deletionMutation.isPending
                    ? "Deleting..."
                    : "Delete My Account"}
                </Button>
              </DialogFooter>
            </form>
          </>
        )}

        {step === 3 && (
          <>
            <DialogHeader>
              <DialogTitle>Processing Deletion...</DialogTitle>
              <DialogDescription>
                Please wait while we process your account deletion request.
              </DialogDescription>
            </DialogHeader>

            <div className="flex items-center justify-center py-6">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-muted border-t-primary" />
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
